import moment from 'moment';
import { Table } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import * as T from '../../../components/Typography';
import * as S from './style';
import { navRoutes } from '../../../constants';
import { useGetLearnersCount } from '../../../api-calls/learners.queries';

const badgesStyle = {
  REFERRAL: {
    text: 'Referral',
    bgColor: 'secondaryMain',
  },
  REJECTED: {
    text: 'Rejected',
    bgColor: 'neutralMain',
  },
  AWAITING_COACH: {
    text: 'Awaiting Coach',
    bgColor: 'secondaryMain',
  },
  TO_BE_ONBOARDED: {
    text: 'Still to onboard',
    bgColor: 'primaryMain',
  },
  ACTIVE: {
    text: 'Active',
    bgColor: 'quaternaryMain',
  },
  INACTIVE: {
    text: 'Inactive',
    bgColor: 'neutralMain',
  },
};

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const awaitingCoach = (referralStatus, assignedToCoach, isReferral, active) => {
  if (!active) return false;

  if (assignedToCoach) return false;

  if (referralStatus === 'APPROVED') {
    return true;
  }
  if (!isReferral) {
    return true;
  }

  return false;
};

const toBeOnboarded = (assignedToCoach, onboarded, active) => {
  if (!active) return false;

  if (!assignedToCoach) return false;

  if (!onboarded) {
    return true;
  }

  return false;
};

const assigned = (assignedToCoach, onboarded, active) => {
  if (!active) return false;

  if (assignedToCoach && onboarded) {
    return true;
  }

  return false;
};

const inactive = (active) => {
  return !active;
};

const decideSingleStatus = (row) => {
  if (row.referralStatus === 'PENDING') return 'REFERRAL';
  if (row.referralStatus === 'REJECTED') return 'REJECTED';

  const isAwaitingCoach = awaitingCoach(
    row.referralStatus,
    row.assignedToCoach,
    row.isReferral,
    row.active
  );

  if (isAwaitingCoach) return 'AWAITING_COACH';

  const isToBeOnboarded = toBeOnboarded(
    row.assignedToCoach,
    row.onboarded,
    row.active
  );

  if (isToBeOnboarded) return 'TO_BE_ONBOARDED';

  const isAssigned = assigned(row.assignedToCoach, row.onboarded, row.active);

  if (isAssigned) return 'ACTIVE';

  const isInactive = inactive(row.active);

  if (isInactive) return 'INACTIVE';
};

const decideMultipleStatuses = (row) => {
  let statuses = [];

  if (row.active === false) {
    statuses.push('INACTIVE');
    return statuses;
  }

  if (row.isReferral) {
    statuses.push('REFERRAL');
  }
  if (row.referralStatus === 'REJECTED') {
    return ['REJECTED'];
  }
  if (!row.assignedToCoach) {
    statuses.push('AWAITING_COACH');
  }
  if (row.assignedToCoach && !row.onboarded) {
    statuses.push('TO_BE_ONBOARDED');
  }
  if (row.assignedToCoach && row.onboarded) {
    statuses.push('ACTIVE');
  }

  return statuses;
};

const columns = (showSingleStatus, countData) => [
  {
    title: 'Name',
    width: '15%',
    dataIndex: 'fullName',
    key: 'fullName',
    render: (fullName, record) => {
      const { referralStatus, id, deleted } = record;
      const needsReview = referralStatus === 'PENDING';

      return (
        <S.LearnerNameColumnWrapper>
          <T.P>{deleted ? 'Deleted' : fullName}</T.P>
          {!deleted ? (
            <T.Link
              to={
                needsReview
                  ? navRoutes.FACILITATOR.LEARNER_REFERRAL.replace(':id', id)
                  : navRoutes.FACILITATOR.LEARNER.replace(':userId', id)
              }
            >
              <S.Button type="text" icon={<EyeOutlined />}>
                {needsReview ? 'Review' : 'View more'}
              </S.Button>
            </T.Link>
          ) : null}
        </S.LearnerNameColumnWrapper>
      );
    },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    filters: [
      {
        text: `All - ${countData?.total || 0}`,
        value: 'all',
      },
      {
        text: `Processing - ${countData?.processingCount || 0}`,
        value: 'processing_status',
      },
      {
        text: `Active - ${countData?.activeCount || 0}`,
        value: 'active_status',
      },
      {
        text: `In Active - ${countData?.inActiveCount || 0}`,
        value: 'in_active_status',
      },
    ],
    defaultFilteredValue: ['all'],
    filterMultiple: false,
    onFilter: (value, record) => true,
    render: (_, row) => {
      if (showSingleStatus) {
        const status = decideSingleStatus(row);

        const { text, bgColor } = badgesStyle[status] || {};
        return <S.Tag bgColor={bgColor}>{text}</S.Tag>;
      } else {
        const statuses = decideMultipleStatuses(row);

        return statuses.map((status) => {
          const { text, bgColor } = badgesStyle[status] || {};
          return (
            <S.Tag bgColor={bgColor} key={status}>
              {text}
            </S.Tag>
          );
        });
      }
    },
  },
  {
    title: 'Control / Treatment',
    dataIndex: 'controlTreatmentType',
    key: 'controlTreatmentType',
    render: (text) => (text ? capitalizeFirstLetter(text) : 'N/A'),
  },
  {
    title: 'Assigned Coach',
    dataIndex: 'coach',
    key: 'coach',
    render: (coach) => (coach.deleted ? 'Deleted' : coach.fullName || 'N/A'),
  },
  {
    title: 'Date added to platform',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (date) => (date ? moment(date).format('DD MMMM YYYY') : 'N/A'),
  },
  {
    title: 'Learner ID',
    dataIndex: 'learnerUniqueId',
    key: 'learnerUniqueId',
  },
  {
    title: 'Latest note',
    dataIndex: 'latestNote',
    key: 'latestNote',
    render: (text) => (text ? <S.TableNotes>{text}</S.TableNotes> : 'N/A'),
  },
];

const LearnersTable = ({
  dataSource,
  onChange,
  dashboard,
  pageSize,
  loading,
}) => {
  const { data: countData } = useGetLearnersCount();

  const showSingleStatus = dashboard;
  const pagination = dashboard
    ? false
    : {
        showSizeChanger: false,
        pageSize,
        total: dataSource?.[0]?.total || 0,
      };
  return (
    <S.TableWrapper>
      <Table
        columns={columns(showSingleStatus, countData)}
        dataSource={dataSource}
        pagination={pagination}
        onChange={onChange}
        loading={loading}
      />
    </S.TableWrapper>
  );
};

export default LearnersTable;
